export const COMPENSATION_ELEMENTS = Object.freeze({
    Salary: { current: 'salary', prior: 'priorSalary', yoy: 'salaryYoY', history: 'Salary' },
    'Target Bonus': { current: 'bonus', prior: 'priorBonus', yoy: 'bonusYoY', history: 'TargetBonus' },
    'Target Total Cash': { current: 'totalCash', prior: 'priorTotalCash', yoy: 'totalCashYoY', history: 'TargetTotalCash' },
    'Annual LTI': { current: 'lti', prior: 'priorLTI', yoy: 'ltiYoY', history: 'AnnualLTI' },
    'Target TDC': { current: 'tdc', prior: 'priorTDC', yoy: 'tdcYoY', history: 'TargetTDC' },
});

export const ANNUAL_LTI_VEHICLE_CATEGORIES = Object.freeze([
    { label: 'Performance Awards', codes: ['PBRS', 'LTC', 'PSO'] },
    { label: 'Time-Vesting Restricted Stock Awards', codes: ['TBRS'] },
    { label: 'Stock Options / Appreciation Awards', codes: ['SO', 'CSAR', 'SSAR'] },
]);

const annualLtiVehicleCategoryByCode = Object.freeze(Object.fromEntries(
    ANNUAL_LTI_VEHICLE_CATEGORIES.flatMap(({ label, codes }) => codes.map((code) => [code, label])),
));

export const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

export const percentile = (values, proportion) => {
    const sorted = values.filter(isFiniteNumber).sort((left, right) => left - right);
    if (!sorted.length) return null;
    if (sorted.length === 1) return sorted[0];
    const position = (sorted.length - 1) * proportion;
    const lower = Math.floor(position);
    const remainder = position - lower;
    return sorted[lower + 1] === undefined
        ? sorted[lower]
        : sorted[lower] + remainder * (sorted[lower + 1] - sorted[lower]);
};

export const median = (values) => percentile(values, 0.5);

export const rowIdentity = (row) => row.observationId ?? `${row.ticker}|${row.roleCode}`;

const distinctCompanies = (rows) => new Set(rows.map((row) => row.ticker)).size;

const distributionMarkers = (values) => {
    if (values.length < 5) return [
        ['Min', percentile(values, 0)],
        ['Median', percentile(values, 0.5)],
        ['Max', percentile(values, 1)],
    ];
    if (values.length < 10) return [
        ['P25', percentile(values, 0.25)],
        ['Median', percentile(values, 0.5)],
        ['P75', percentile(values, 0.75)],
    ];
    return [
        ['P10', percentile(values, 0.1)],
        ['P25', percentile(values, 0.25)],
        ['Median', percentile(values, 0.5)],
        ['P75', percentile(values, 0.75)],
        ['P90', percentile(values, 0.9)],
    ];
};

const buildTrend = (rows, historyRows, element) => {
    const historyKey = COMPENSATION_ELEMENTS[element].history;
    const complete = rows.map((row) => {
        const history = historyRows
            .filter((item) => (item.Observation_Key ?? `${item.Ticker}|${item.roleCode}`) === rowIdentity(row))
            .sort((left, right) => String(right.FYE).localeCompare(String(left.FYE)));
        const values = history.slice(0, 3).map((item) => {
            if (element === 'Target TDC') {
                if (!isFiniteNumber(item.Salary)) return null;
                return item.Salary
                    + (isFiniteNumber(item.TargetBonus) ? item.TargetBonus : 0)
                    + (isFiniteNumber(item.AnnualLTI) ? item.AnnualLTI : 0);
            }
            return isFiniteNumber(item[historyKey]) ? item[historyKey] : null;
        });
        return { row, history: history.slice(0, 3), values };
    }).filter((entry) => entry.values.length === 3 && entry.values.every(isFiniteNumber));
    if (complete.length < 3) return { points: [], executiveCount: complete.length, companyCount: distinctCompanies(complete.map((entry) => entry.row)) };
    const labels = ['Two Years Prior', 'Prior Year', 'Most Recent'];
    const points = labels.map((period, index) => {
        const value = median(complete.map((entry) => entry.values[2 - index]));
        return { period, value, yoy: null };
    });
    points.forEach((point, index) => {
        if (index === 0 || !isFiniteNumber(points[index - 1].value) || points[index - 1].value === 0) return;
        point.yoy = point.value / points[index - 1].value - 1;
    });
    return { points, executiveCount: complete.length, companyCount: distinctCompanies(complete.map((entry) => entry.row)) };
};

const buildAnnualLtiMix = (rows) => {
    const executives = rows.flatMap((row) => {
        const byExecutive = new Map();
        (row.annualLtiVehicles ?? []).forEach((item) => {
            if (!item.executive || !item.vehicle || !isFiniteNumber(item.value) || !isFiniteNumber(item.share)) return;
            const category = annualLtiVehicleCategoryByCode[String(item.vehicle).trim().toUpperCase()];
            if (!category) return;
            if (!byExecutive.has(item.executive)) byExecutive.set(item.executive, { shares: {}, values: {} });
            const detail = byExecutive.get(item.executive);
            detail.shares[category] = (detail.shares[category] ?? 0) + item.share;
            detail.values[category] = (detail.values[category] ?? 0) + item.value;
        });
        return Array.from(byExecutive.entries()).map(([executive, detail]) => ({
            row,
            executive,
            shares: detail.shares,
            values: detail.values,
            annualLti: Object.values(detail.values).reduce((sum, value) => sum + value, 0),
        }));
    });
    const vehicles = ANNUAL_LTI_VEHICLE_CATEGORIES
        .map(({ label }) => label)
        .filter((category) => executives.some((entry) => Object.prototype.hasOwnProperty.call(entry.shares, category)));
    const mix = vehicles.map((vehicle) => ({
        vehicle,
        share: executives.length ? executives.reduce((sum, entry) => sum + (entry.shares[vehicle] ?? 0), 0) / executives.length : null,
        averageValue: executives.length ? executives.reduce((sum, entry) => sum + (entry.values[vehicle] ?? 0), 0) / executives.length : null,
    }));
    const companyCount = distinctCompanies(executives.map((entry) => entry.row));
    const prevalence = ANNUAL_LTI_VEHICLE_CATEGORIES.map(({ label }) => {
        const categoryCompanyCount = new Set(executives
            .filter((entry) => Object.prototype.hasOwnProperty.call(entry.shares, label))
            .map((entry) => entry.row.ticker)).size;
        return {
            vehicle: label,
            companyCount: categoryCompanyCount,
            prevalence: companyCount ? categoryCompanyCount / companyCount : null,
        };
    });
    return { mix, prevalence, details: executives, vehicles, executiveCount: executives.length, companyCount };
};

export const buildPeerAnalytics = (includedRows, historyRows, selectedElement) => {
    const payLevelElements = Object.entries(COMPENSATION_ELEMENTS).flatMap(([label, config]) => label === 'Target Bonus'
        ? [[label, config, 'money'], ['Target Bonus % Salary', { current: 'bonusPct' }, 'percent']]
        : [[label, config, 'money']]);
    const payLevels = payLevelElements.map(([label, config, format]) => {
        const values = includedRows.map((row) => row[config.current]).filter(isFiniteNumber);
        return { label, format, count: values.length, p25: percentile(values, 0.25), median: median(values), p75: percentile(values, 0.75) };
    });
    const payMixRows = includedRows.filter((row) => isFiniteNumber(row.tdc) && row.tdc > 0 && [row.salary, row.bonus, row.lti].every(isFiniteNumber));
    const rawMix = {
        salary: median(payMixRows.map((row) => row.salary / row.tdc)),
        bonus: median(payMixRows.map((row) => row.bonus / row.tdc)),
        lti: median(payMixRows.map((row) => row.lti / row.tdc)),
    };
    const mixTotal = Object.values(rawMix).filter(isFiniteNumber).reduce((sum, value) => sum + value, 0);
    const payMix = Object.fromEntries(Object.entries(rawMix).map(([key, value]) => [key, mixTotal && isFiniteNumber(value) ? value / mixTotal : null]));
    const config = COMPENSATION_ELEMENTS[selectedElement];
    const validRows = includedRows.filter((row) => isFiniteNumber(row[config.current]));
    const yoyRows = includedRows.filter((row) => isFiniteNumber(row[config.current]) && isFiniteNumber(row[config.prior]));
    const incumbents = yoyRows.filter((row) => row.incumbent);
    const values = validRows.map((row) => row[config.current]);
    const yoyPercent = (row) => isFiniteNumber(row[config.yoy]) ? row[config.yoy] : row[config.prior] === 0 ? null : row[config.current] / row[config.prior] - 1;
    const rankedRows = validRows.slice().sort((left, right) => right[config.current] - left[config.current]).map((row, index, sorted) => ({
        ...row,
        rank: sorted.findIndex((candidate) => candidate[config.current] === row[config.current]) + 1,
        rankTotal: sorted.length,
        selectedValue: row[config.current],
    }));
    const transitionTypes = ['Internal Promotion', 'External Hire'];
    const transitions = transitionTypes.map((transitionType) => {
        const rows = includedRows.filter((row) => row.transitionType === transitionType);
        const companies = distinctCompanies(rows);
        const companiesWithAwards = new Set(rows.filter((row) => (row.specials ?? []).length).map((row) => row.ticker)).size;
        return {
            transitionType, rows, executiveCount: rows.length, companyCount: companies,
            median: median(rows.map((row) => row[config.current])),
            medianYoy: median(rows.map(yoyPercent)),
            companiesWithAwards,
            specialAwardPercent: companies ? companiesWithAwards / companies : null,
        };
    });
    const specialRows = includedRows.flatMap((row) => (row.specials ?? []).map((award) => ({ ...award, ticker: row.ticker, company: row.company, roleCode: row.roleCode })));
    const specialCompanyCount = new Set(specialRows.map((row) => row.ticker)).size;
    const awardTypes = Array.from(new Set(specialRows.map((row) => row.grantType))).filter(Boolean).sort().map((grantType) => {
        const rows = specialRows.filter((row) => row.grantType === grantType);
        const companyCount = new Set(rows.map((row) => row.ticker)).size;
        const denominator = distinctCompanies(includedRows);
        return { grantType, rows, companyCount, percent: denominator ? companyCount / denominator : null };
    });
    return {
        companyCount: distinctCompanies(includedRows), executiveCount: includedRows.length,
        payLevels, payMix, payMixCount: payMixRows.length, payMixCompanyCount: distinctCompanies(payMixRows),
        element: {
            median: median(values), count: validRows.length, companyCount: distinctCompanies(validRows),
            medianYoy: median(yoyRows.map(yoyPercent)), medianDollarChange: median(yoyRows.map((row) => row[config.current] - row[config.prior])),
            yoyCount: yoyRows.length, yoyCompanyCount: distinctCompanies(yoyRows),
            incumbentMedianYoy: median(incumbents.map(yoyPercent)), incumbentMedianDollarChange: median(incumbents.map((row) => row[config.current] - row[config.prior])),
            incumbentCount: incumbents.length, incumbentCompanyCount: distinctCompanies(incumbents),
            distribution: { markers: distributionMarkers(values), rows: rankedRows },
            trend: buildTrend(includedRows, historyRows, selectedElement),
        },
        transitions,
        specials: { rows: specialRows, awardTypes, companyCount: specialCompanyCount, percent: distinctCompanies(includedRows) ? specialCompanyCount / distinctCompanies(includedRows) : null },
        annualLtiMix: buildAnnualLtiMix(includedRows),
    };
};
